/**
 * HTTP client. Uses fetch (no axios). Auto-handles 401 -> clear + redirect.
 */

const BASE = '';

export class ApiError extends Error {
  status: number;
  code: string;
  body: unknown;
  constructor(status: number, code: string, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  params?: Record<string, string | number | undefined>;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let url = path;
  if (opts.params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.params)) {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    }
    const q = qs.toString();
    if (q) url += (url.includes('?') ? '&' : '?') + q;
  }
  const token = localStorage.getItem('wsb_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers ?? {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
  };
  if (opts.body !== undefined && opts.body !== null) {
    init.body = JSON.stringify(opts.body);
  }
  if (opts.signal) {
    init.signal = opts.signal;
  }
  const resp = await fetch(BASE + path, init);
  if (resp.status === 204) {
    return null as T;
  }
  const text = await resp.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!resp.ok) {
    if (resp.status === 401) {
      localStorage.removeItem('wsb_token');
      localStorage.removeItem('wsb_refresh');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    let code = 'HTTP_ERROR';
    let message = resp.statusText || `请求失败（HTTP ${resp.status}）`;
    if (body && typeof body === 'object') {
      const b = body as {
        detail?: unknown;
        code?: string;
        error_code?: string;
        message?: string;
      };
      if (b.detail && typeof b.detail === 'object') {
        const d = b.detail as {code?: string; error_code?: string; message?: string};
        if (d.code || d.error_code) code = d.code || d.error_code || code;
        if (d.message) message = d.message;
        if (Array.isArray(b.detail)) {
          code = 'VALIDATION_ERROR';
          message = '提交信息有误，请检查填写内容后重试。';
        }
      } else if (typeof b.detail === 'string') {
        message = b.detail;
      } else if (b.code || b.error_code) {
        code = b.code || b.error_code || code;
        message = b.message ?? message;
      }
    }
    throw new ApiError(resp.status, code, message, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, {...opts, method: 'GET'}),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, {...opts, method: 'POST', body}),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, {...opts, method: 'PUT', body}),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, {...opts, method: 'PATCH', body}),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, {...opts, method: 'DELETE'}),
};
